import * as React from 'react';
import { EnterUniversal, WarningFilled } from '@duxton-web/icons';
import { Alert, Divider, Select, Tag, Tooltip } from '@duxton-web/ui';
import Fuse from 'fuse.js';
import { debounce, uniq, uniqWith } from 'lodash';

import { ControlledInputPropsBase } from '../../../common/InputControlProps';
import Service, { City, Vehicle } from '../../../lib/App';
import { valueToList } from '../../../pages/RolloutTab/RolloutEditor/RolloutBlock/ConstraintBlocks/ConstraintBlock/ConstraintValueInput/SelectTree/treeUtils';
import { extractInput, selectRecordsFromPage } from './fuseSelectUtil';

import './FuseSelect.css';

const { Option } = Select;

interface FuseSelectState {
  showVehicles: Vehicle[]; // Items to be shown in options
  searchVehicles: Vehicle[]; // Items saved from the latest search
  listInput: string; // last search input that is list
  page: number; // current page displayed in option
  warningMessage: string; // nonexisted id in the input list
  singleSearchQuery: string; // last search query
}

export interface FuseSelectProps extends ControlledInputPropsBase<string> {
  keys: string[];
  mode?: 'multiple' | 'tags' | null;
}

const listInputRegxp: RegExp = /^(\d+\s*)(,\s*\d+\s*)+$/;
const RECORD_PER_PAGE = 20;

export default class FuseSelectComponent extends React.Component<
  FuseSelectProps,
  FuseSelectState
> {
  static defaultProps = {
    keys: ['id', 'name', 'cityID', 'vehicleType'],
    mode: null,
  };

  private fuse: Fuse<Vehicle, any>;
  private vehiclesMap: Map<number, Vehicle>;
  private vehiclesList: Vehicle[];
  private citiesMap: Map<number, City>;
  private selectComponent: any;

  constructor(props: FuseSelectProps) {
    super(props);
    const searchKeys = this.props.keys.filter((key) => key !== 'cityID');

    this.citiesMap = Service.cache!.getCitiesMap();

    this.vehiclesList = Service.cache!.getAllVehicles().sort((a, b) => {
      const aCityName = this.citiesMap.get(a.cityID)?.name;
      const bCityName = this.citiesMap.get(b.cityID)?.name;
      return aCityName.localeCompare(bCityName);
    });

    this.fuse = new Fuse(this.vehiclesList, {
      keys: searchKeys,
    });
    this.state = {
      showVehicles: selectRecordsFromPage(0, 1, this.vehiclesList, 20),
      searchVehicles: this.vehiclesList,
      page: 1,
      warningMessage: '',
      listInput: '',
      singleSearchQuery: '',
    };

    this.vehiclesMap = new Map();
    this.vehiclesList.forEach((v) => {
      this.vehiclesMap.set(v.id, v);
    });

    this.handleOnSearch = debounce(this.handleOnSearch, 50);
  }

  reset = () => {
    this.setState({
      showVehicles: selectRecordsFromPage(0, 1, this.vehiclesList, 20),
      searchVehicles: this.vehiclesList,
      page: 1,
      warningMessage: '',
      listInput: '',
      singleSearchQuery: '',
    });
  };

  handleChange = (value) => {
    if (!this.props.mode) {
      this.props.onChange(`${value}`);

      return;
    }

    this.props.onChange('[' + value.join(',') + ']');
  };

  handleOnSearch = (query: string) => {
    // reset if query is empty
    if (query == '') {
      this.setState({
        warningMessage: '',
        page: 1,
        showVehicles: selectRecordsFromPage(
          0,
          1,
          this.vehiclesList,
          RECORD_PER_PAGE,
        ),
        searchVehicles: this.vehiclesList,
        listInput: '',
        singleSearchQuery: '',
      });
      return;
    }

    // list input detected, set list input string to state
    if (listInputRegxp.test(query)) {
      this.setState({
        ...this.state,
        searchVehicles: [],
        showVehicles: [],
        listInput: query,
      });
      return;
    }

    // first time search, put the search result to search array and pop up the first page
    let items: any[] = [];
    let results = this.fuse.search(query);
    for (let index = 0; index < results.length; index++) {
      items.push(results[index]);
    }
    let searchItems = selectRecordsFromPage(0, 1, items, RECORD_PER_PAGE);
    this.setState({
      ...this.state,
      showVehicles: searchItems,
      searchVehicles: items,
      listInput: '',
      warningMessage: '',
      page: 1,
      singleSearchQuery: query,
    });
  };

  handleListSelect = (e: { keyCode: number }) => {
    if (e.keyCode !== 13) {
      return;
    }

    // single mode
    if (this.props.mode === null) {
      if (this.state.singleSearchQuery.includes(',')) {
        this.selectComponent.blur();
        this.reset();
        return;
      }
      const vehicleId = parseInt(this.state.singleSearchQuery, 10);
      if (!isNaN(vehicleId) && this.vehiclesMap.has(vehicleId)) {
        this.props.onChange(`${vehicleId}`);
      }
      this.reset();
      return;
    }

    // multiple mode
    if (listInputRegxp.test(this.state.listInput) && this.props.mode !== null) {
      let filterResult = extractInput(this.state.listInput, this.vehiclesMap);

      if (filterResult.notExists.length != 0) {
        this.setState({
          ...this.state,
          warningMessage: `The following ids do not exist, please remove them to continue select: ${filterResult.notExists}`,
        });
        return;
      }
      // let newValues = [...values, ...input];
      let newValues = [
        ...valueToList(this.props.value!),
        ...filterResult.input,
      ];
      newValues = uniq(newValues);
      this.props.onChange('[' + newValues.join(',') + ']');
      this.selectComponent.blur();
      // eslint-disable-next-line @typescript-eslint/no-this-alias
      const self = this;
      setTimeout(() => {
        self.selectComponent.focus();
      }, 100);
      this.setState({
        warningMessage: '',
        page: 1,
        showVehicles: selectRecordsFromPage(
          0,
          1,
          this.vehiclesList,
          RECORD_PER_PAGE,
        ),
        searchVehicles: this.vehiclesList,
        listInput: '',
      });
    }
  };

  loadNext = () => {
    let newItems = selectRecordsFromPage(
      this.state.page,
      this.state.page + 1,
      this.state.searchVehicles,
      RECORD_PER_PAGE,
    );

    this.setState({
      ...this.state,
      page: this.state.page + 1,
      showVehicles: newItems,
    });
  };

  loadPrev = () => {
    let oldItems = selectRecordsFromPage(
      this.state.page - 2,
      this.state.page - 1,
      this.state.searchVehicles,
      RECORD_PER_PAGE,
    );

    this.setState({
      ...this.state,
      page: this.state.page - 1,
      showVehicles: oldItems,
    });
  };

  renderDropdown = (
    menu:
      | boolean
      // eslint-disable-next-line @typescript-eslint/ban-types
      | {}
      | any[]
      | React.ReactChild
      | React.ReactPortal
      | null
      | undefined,
  ) => {
    let listSize = this.state.showVehicles.length;
    let fromItemOrder = (this.state.page - 1) * RECORD_PER_PAGE + 1;
    let toItemOrder = (this.state.page - 1) * RECORD_PER_PAGE + listSize;
    if (this.state.listInput === '') {
      return (
        <div>
          {React.isValidElement(menu) ||
          typeof menu === 'string' ||
          typeof menu === 'number' ||
          Array.isArray(menu)
            ? menu
            : null}
          {listSize > 0 && (
            <React.Fragment>
              <Divider className="dropdown-divider" />
              <div className="items-order-message">
                {`Showing items ${fromItemOrder} to ${toItemOrder} of ${this.state.searchVehicles.length}`}
              </div>
              <div
                style={{
                  float: 'right',
                  marginRight: '10px',
                }}
              >
                {this.state.page >= 2 && (
                  <a
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={this.loadPrev}
                  >
                    {' '}
                    prev{' '}
                  </a>
                )}
                {this.state.page <=
                  Math.floor(
                    this.state.searchVehicles.length / RECORD_PER_PAGE,
                  ) && (
                  <a
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={this.loadNext}
                  >
                    {' '}
                    next{' '}
                  </a>
                )}
              </div>
            </React.Fragment>
          )}
        </div>
      );
    }
    // single mode
    if (this.props.mode === null) {
      return (
        <div className="list-input-detection-message">
          Multiple inputs not allowed
        </div>
      );
    }
    // multiple mode
    return (
      <div className="list-input-detection-message">
        List input detected, press <EnterUniversal />
        (ENTER) to select
      </div>
    );
  };

  render() {
    let valuesArr = valueToList(this.props.value!);
    let selected: Vehicle[] = [];

    valuesArr.forEach((value: string) => {
      let id = parseInt(value, 10);
      if (this.vehiclesMap.has(id)) {
        selected.push(this.vehiclesMap.get(id)!);
      } else {
        // create invalid vehicle
        let invalidVehicle: Vehicle = {
          id: id,
          cityID: -1,
          icon: '',
          name: '',
          serviceType: '',
          active: false,
        };
        selected.push(invalidVehicle);
      }
    });

    let optionItems = [...this.state.showVehicles, ...selected];
    optionItems = uniqWith(optionItems, (a, b) => {
      return a.id == b.id;
    });
    const options = optionItems.map((v) => {
      if (v != null && v.id != null) {
        if (this.vehiclesMap.has(v.id)) {
          return (
            <Option key={v.id} value={String(v.id)} title={`${v.name}-${v.id}`}>
              {' '}
              {v.name}-{v.id}{' '}
              <div className="option-tag-group">
                <Tag size="xsmall" className="cities-tag">
                  {this.citiesMap.get(v.cityID)!.name}
                </Tag>

                {!v.active && (
                  <Tooltip title="inactive vehicle">
                    <WarningFilled className="inactive-icon" type="warning" />
                  </Tooltip>
                )}
              </div>{' '}
            </Option>
          );
        } else {
          return (
            <Option
              key={v.id}
              value={String(v.id)}
              title={`${v.id}❗(deleted)`}
            >
              {v.id}❗(deleted)
            </Option>
          );
        }
      }
    });
    return (
      <div>
        {this.state.warningMessage.length != 0 && (
          <Alert type="warning" message={this.state.warningMessage} />
        )}
        <div onKeyDown={this.handleListSelect}>
          <Select
            mode={this.props.mode}
            className="select-component-fuseselect"
            placeholder={this.props.mode ? 'Select Multiple' : ''}
            onSearch={this.handleOnSearch}
            onChange={this.handleChange}
            showSearch
            filterOption={false}
            value={valueToList(this.props.value!)}
            dropdownRender={this.renderDropdown}
            disabled={this.props.disabled}
            ref={(t) => (this.selectComponent = t)}
            showAction={['click', 'focus']}
            onBlur={this.reset}
            optionLabelProp="title"
            style={{ width: 'fit-content', minWidth: 200 }}
            popupMatchSelectWidth={false}
          >
            {options}
          </Select>
        </div>
      </div>
    );
  }
}
